/**
 * ClosedHoursDialog · v2.8+E303
 * ─────────────────────────────
 * Driver-portal "We Are Currently Closed" popup. Fires automatically when the
 * business is outside operating hours (same live schedule as the rider portal,
 * via usePortalHours → WS + poll, so admin edits reflect automatically).
 *
 * NON-BLOCKING: dismissing it (Close button or the X) simply lets the driver
 * through normally — it never locks anyone out.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";
import usePortalHours from "@/hooks/usePortalHours";

export default function ClosedHoursDialog({ active = true }) {
  const hours = usePortalHours();
  const [dismissed, setDismissed] = useState(false);
  const closed = !!(hours && hours.is_open === false);
  const open = active && closed && !dismissed;

  const time = (hours?.hours_label || "10:00 AM – 3:00 AM").replace(/\s*(CDT|CST)\b/i, "").trim();
  const days = hours?.days_label || "7 days a week";
  const tz = hours?.tz_abbrev || "CDT";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setDismissed(true); }}>
      <DialogContent data-testid="driver-closed-dialog" className="bg-zinc-900 border-amber-400/40 max-w-md text-center">
        <div className="mx-auto h-14 w-14 rounded-full bg-amber-400/10 border border-amber-400/40 flex items-center justify-center">
          <Clock className="h-7 w-7 text-amber-300" />
        </div>
        <DialogTitle className="text-xl font-extrabold text-zinc-100 text-center" data-testid="driver-closed-heading">
          We Are Currently Closed
        </DialogTitle>
        <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3">
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300 font-bold mb-1">Business Hours</div>
          <div className="text-base font-bold text-zinc-100" data-testid="driver-closed-hours">{days}, {time}</div>
          <div className="text-xs text-zinc-500">Central Time ({tz})</div>
        </div>
        <DialogDescription className="text-sm text-zinc-300 text-center">
          Please log in or call back during our business hours.
        </DialogDescription>
        <Button
          onClick={() => setDismissed(true)}
          data-testid="driver-closed-dismiss"
          className="w-full h-11 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
        >
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
}
